import React, { useState, useCallback } from 'react';
import { RealtimeProvider, useRealtime } from './contexts/RealtimeContext';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { OrgTree } from './components/OrgTree';
import { AgentCard } from './components/AgentCard';
import { TaskBoard } from './components/TaskBoard';
import { TaskDetail } from './components/TaskDetail';
import { ActivityLog } from './components/ActivityLog';
import { DecisionLog } from './components/DecisionLog';
import { SettingsPanel } from './components/SettingsPanel';
import styles from './styles/App.module.css';

const TABS = {
  org: 'Org',
  tasks: 'Tasks',
  activity: 'Activity',
  decisions: 'Decisions',
  settings: 'Settings',
};

function Dashboard() {
  const {
    data,
    selectedAgentId,
    selectedTaskId,
    currentTab,
    selectAgent,
    selectTask,
    setTab,
    connection,
  } = useRealtime();

  const handleCloseDetail = useCallback(() => {
    selectAgent(null);
    selectTask(null);
  }, [selectAgent, selectTask]);

  const detailOpen = Boolean(selectedAgentId || selectedTaskId);

  return (
    <div className={styles.app}>
      <Header tabs={TABS} activeTab={currentTab} onTabChange={setTab} mode={data?.status?.company_mode} />

      <Sidebar>
        <OrgTree
          agents={data?.agents?.agents || []}
          branches={data?.agents?.branches || []}
          selectedId={selectedAgentId}
          onSelect={selectAgent}
        />
      </Sidebar>

      <main className={styles.main} id="main-content">
        {connection.reconnecting && (
          <div className={styles.banner} role="status" aria-live="polite">
            Reconnecting…
          </div>
        )}
        {connection.stale && !connection.reconnecting && (
          <div className={`${styles.banner} ${styles.bannerStale}`} role="status" aria-live="polite">
            Data may be stale. Reconnecting…
          </div>
        )}

        {currentTab === 'org' && (
          <OrgTree
            agents={data?.agents?.agents || []}
            branches={data?.agents?.branches || []}
            selectedId={selectedAgentId}
            onSelect={selectAgent}
          />
        )}
        {currentTab === 'tasks' && (
          <TaskBoard tasks={data?.tasks} onSelectTask={selectTask} />
        )}
        {currentTab === 'activity' && <ActivityLog events={data?.activity?.events || []} />}
        {currentTab === 'decisions' && <DecisionLog decisions={data?.decisions?.decisions || []} />}
        {currentTab === 'settings' && <SettingsPanel config={data?.config} status={data?.status} />}
      </main>

      {detailOpen && (
        <aside className={styles.detailPanel} aria-label="Detail panel">
          {selectedAgentId && (
            <AgentCard
              agentId={selectedAgentId}
              onClose={handleCloseDetail}
            />
          )}
          {selectedTaskId && (
            <TaskDetail
              taskId={selectedTaskId}
              onAgentClick={selectAgent}
              onTaskClick={selectTask}
              onClose={handleCloseDetail}
            />
          )}
        </aside>
      )}
    </div>
  );
}

export default function App() {
  const [auth] = useState({
    role: 'ceo',
    agentId: 'ceo',
    token: null,
  });

  return (
    <RealtimeProvider auth={auth}>
      <Dashboard />
    </RealtimeProvider>
  );
}
